import ApplicationCooLayout from "../../layout/application-coo-layout";
import { SectionCard } from "../../components/card";
import { P2Alert, P2Button, P2ButtonGroup, P2TextDescription, P2TextLabel } from "react-bootstrap-common-script";
import RadioGroup from "../../components/form/grouped/radio/radio-group";
import { Separator } from "../../components/separator";
import { FinancialCalculator } from "../../models/classes/financial-calculator/FinancialCalculator.class";
import { validateFinancialCalculator } from "../../utils/validators/financial-calculator/validator";
import { useNavigate } from "react-router-dom";
import { useForm } from "../../hooks/use-form";
import { Dropdown } from "../../components/dropdown";
import { financialCalculatorFlatTypeItems } from "../../utils/shared-dropdown";
import { FlatRoomTypeEnum } from "../../models/enum/flat-room-type.enum";
import { Helpers } from "../../utils/helper";
import "./estimate-fees.scss";
import useIsMobile from "../../hooks/use-is-mobile";
import { useEffect, useRef, useState } from "react";
import { PolymerAxios } from "../../utils/axios";
import { API_EstimatedFees } from "../../utils/api";
import { EstimatedFees } from "./types";
import { useAppSelector } from "../../redux/hook";
import config from "../../config";
import { EXAMPLE_FEES } from "./example-fees";

function round(value: number, roundingType: "up" | "down" | "nearest" = "nearest", smallestUnit = 0.01) {
  switch (roundingType) {
    case "up": {
      if (smallestUnit === 1) {
        return Math.ceil(value);
      }
      return Math.ceil(value / smallestUnit) * smallestUnit;
    }
    case "down": {
      if (smallestUnit === 1) {
        return Math.floor(value);
      }
      return Math.floor(value / smallestUnit) * smallestUnit;
    }
    default: {
      if (smallestUnit === 1) {
        return Math.round(value);
      }
      return Math.round(value / smallestUnit) * smallestUnit;
    }
  }
}

function getScaledValue(value: number) {
  let scaledValue = 0;
  const minValue = 20;

  // First $30,000.00
  scaledValue += Math.min(value, 30_000) * 0.00135;
  value -= 30_000;
  if (value <= 0) return Math.max(Math.ceil(scaledValue), minValue);

  // Next $30,000.00
  scaledValue += Math.min(value, 30_000) * 0.00108;
  value -= 30_000;
  if (value <= 0) return Math.max(Math.ceil(scaledValue), minValue);

  // Thereafter
  scaledValue += value * 0.0009;
  return Math.max(Math.ceil(scaledValue), minValue);
}

interface EstimateFeesProps {
  fromTransfer?: boolean;
}
function EstimateFees({ fromTransfer = true }: EstimateFeesProps) {
  const { isMobile } = useIsMobile();
  const { values, formValid, setFormValue } = useForm(new FinancialCalculator({}), () => {}, validateFinancialCalculator);
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [estimatedFees, setEstimatedFees] = useState<EstimatedFees | undefined>(config.isLocalhost ? EXAMPLE_FEES : undefined);

  const pdfRef = useRef(null);

  const mainTitleFontSize = isMobile ? "18px" : "20px";
  const subTitleFontSize = isMobile ? "16px" : "18px";

  const handleFlatTypeChange = (value: FlatRoomTypeEnum) => {
    setFormValue({ flatType: value });
  };

  const printAsPdf = () => {
    window.print();
  };

  const navigateBack = () => {
    navigate("/dashboard");
  };

  // Desctructure payload
  let {
    general_fees: {
      gst_rate = 0,
      application_fee_with_gst = 0,
      ltv_limit = 0,
      current_owner_misc_fee = 0,
      proposed_owner_misc_fee = 0,
      registration_fee = 0,
      redemption_of_mortgage = 0,
    },
    specific_fees,
  } = estimatedFees || { general_fees: {}, specific_fees: [] };
  gst_rate /= 100; // the gst_rate from api response is in percent, need to divide by 100 to convert to decimal
  ltv_limit /= 100; // the ltv_limit from api response is in percent, need to divide by 100 to convert to decimal

  const targetIndex: number = specific_fees.findIndex((item) => item.flat_type === values.flatType);
  let { base_valuation_fee_with_gst = 0, valuation_admin_fee = 0, average_median_price = 0, tdm = 0 } = specific_fees[targetIndex] ?? {};
  for (let i = targetIndex; i >= 0; i--) {
    const currentSpecificFees = specific_fees[i];
    if (
      (currentSpecificFees.base_valuation_fee_with_gst || currentSpecificFees.base_valuation_fee_with_gst === 0) &&
      (currentSpecificFees.valuation_admin_fee || currentSpecificFees.valuation_admin_fee === 0)
    ) {
      base_valuation_fee_with_gst = currentSpecificFees.base_valuation_fee_with_gst;
      valuation_admin_fee = currentSpecificFees.valuation_admin_fee;
      break;
    }
  }

  // Section 1 (upfront fees)
  application_fee_with_gst = round(application_fee_with_gst);
  const valuationFee = values.isThereNewLoan ? round(base_valuation_fee_with_gst + valuation_admin_fee) : 0;
  const upfrontFees = application_fee_with_gst + valuationFee;

  // Section 2 (current owner fees)
  const processedMedianPrice = round(getScaledValue(average_median_price) * 0.5, "up", 1) * (1 + gst_rate);
  const lawyerFeesForCurrentOwners = round(processedMedianPrice + current_owner_misc_fee);
  const redemptionOfMortgageFee = values.isThereOutstandingLoan ? round(redemption_of_mortgage + tdm * (1 + gst_rate)) : 0;
  const currentOwnersFees = round(lawyerFeesForCurrentOwners + redemptionOfMortgageFee, "nearest", 0.01);

  // Section 3 (outgoing owner fees)
  const lawyerFeesForProposedOwners = round(getScaledValue(average_median_price) * (1 + gst_rate) + proposed_owner_misc_fee + registration_fee);
  const estimateMortgageAmount = average_median_price * ltv_limit;
  const baseMortgageFee = getScaledValue(estimateMortgageAmount) * 1.5;
  const stampDutyFee = Math.min(500, estimateMortgageAmount * 0.004);
  const newMortgageFee = values.isThereNewLoan ? round(baseMortgageFee + stampDutyFee + registration_fee) : 0;
  const proposedOwnersFees = round(lawyerFeesForProposedOwners + newMortgageFee, "nearest", 0.01);

  useEffect(() => {
    if (config.isLocalhost) return;

    const fetchEstimatedFees = async () => {
      setIsLoading(true);
      try {
        const response = await PolymerAxios("", "", "application/json", 0, "", true).get(`${API_EstimatedFees}`);
        const data: EstimatedFees = response.data;
        setEstimatedFees(data);
      } catch (error) {
        console.log("Error fetching estimated fees", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEstimatedFees();
  }, [loginUser?.uinfin, singpassToken]);

  return (
    <ApplicationCooLayout
      title="Estimate Fees for Change in Flat Ownership"
      customPages={fromTransfer ? undefined : [{ title: "Back", link: "https://services2.hdb.gov.sg/webapp/FI10AWESVCLIST/FI10SEServiceList" }]}
    >
      <div ref={pdfRef} className="polymer-application-coo__wrapper">
        {!isLoading && !estimatedFees ? (
          <>
            <div style={{ marginLeft: 38, marginRight: 38, marginBottom: 62 }}>
              <P2Alert variant={"danger"} text={"Unable to fetch estimated fees"} />
            </div>
            <Separator style={{ marginLeft: 38, marginRight: 38, marginBottom: 32 }} />
            <div style={{ marginBottom: 120 }}>
              <P2ButtonGroup>
                <P2Button variant="outline-primary" disabled={false} onClick={navigateBack}>
                  Back
                </P2Button>
              </P2ButtonGroup>
            </div>
          </>
        ) : (
          <>
            <SectionCard
              activeStep={undefined}
              titleChildren={<P2TextDescription title="Check estimated fees" />}
              stepperItems={[]}
              contentChildren={
                <div className="px-4">
                  <div style={{ marginBottom: 32 }}>
                    <div style={{ marginBottom: 20 }}>
                      <P2TextLabel
                        bold="400"
                        color={"#323232"}
                        label={
                          "This service provides an estimation of the administrative, legal and other relevant fees payable to HDB for processing an application for change in flat ownership. " +
                          "This service does not calculate any loan or CPF refund that may be required. You will have to engage either a private lawyer or appoint HDB to act on your behalf in the conveyancing process. " +
                          "The estimated fees payable are based on the scenario that HDB is able to act in the conveyancing process."
                        }
                      />
                    </div>
                    <div style={{ marginBottom: 32 }}>
                      <P2TextLabel
                        bold="400"
                        color={"#323232"}
                        label="You will be informed about the actual fees payable and the financial plan when you apply to change the ownership of the flat."
                      />
                    </div>
                    <Dropdown
                      required
                      selectedDropdown={values.flatType}
                      placeholder="Select flat type"
                      label="What is your flat type?"
                      items={financialCalculatorFlatTypeItems}
                      setSelectedDropdown={handleFlatTypeChange}
                    />
                  </div>
                  {values.flatType ? (
                    <RadioGroup
                      style={{ marginBottom: 32 }}
                      required
                      label="Do the current owners have any outstanding HDB housing loan?"
                      layout="default"
                      items={[
                        { id: 1, description: "Yes", value: true, variant: "default" },
                        { id: 2, description: "No", value: false, variant: "default" },
                      ]}
                      value={values.isThereOutstandingLoan}
                      onItemSelect={(value) => {
                        setFormValue({ isThereOutstandingLoan: value });
                      }}
                    />
                  ) : null}
                  {values.flatType && values.isThereOutstandingLoan !== undefined ? (
                    <RadioGroup
                      style={{ marginBottom: 32 }}
                      required
                      label="Do the proposed owners need to take a new HDB housing loan?"
                      layout="default"
                      items={[
                        { id: 1, description: "Yes", value: true, variant: "default" },
                        { id: 2, description: "No", value: false, variant: "default" },
                      ]}
                      value={values.isThereNewLoan}
                      onItemSelect={(value) => {
                        setFormValue({ isThereNewLoan: value });
                      }}
                    />
                  ) : null}

                  {values.flatType && values.isThereOutstandingLoan !== undefined && values.isThereNewLoan !== undefined ? (
                    <>
                      <Separator style={{ marginBottom: 32 }} />
                      <div style={{ marginBottom: 32 }}>
                        <P2TextLabel bold="400" color={"#323232"} label={"The estimated fees below are inclusive of GST."} />
                      </div>
                      {/* Section 1 */}
                      <div className="polymer-financial-calculator-grid polymer-financial-calculator-fee-card">
                        <P2TextLabel color="#323232" label="Fees payable at point of application" fontSize={mainTitleFontSize} />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={mainTitleFontSize} bold={700} label={Helpers.amountToTwoDecimals(upfrontFees)} />
                        </div>
                        <div className="polymer-financial-calculator-separator" />
                        <P2TextLabel
                          color="#323232"
                          label="Administrative fee"
                          fontSize={subTitleFontSize}
                          hasTooltip
                          tooltipContent={
                            <div className="polymer-financial-calculator-tooltip-content">
                              An administrative fee of $50 is required for the submission of an application of change in flat ownership. There is no refund for
                              the administrative fee once application is submitted.
                            </div>
                          }
                        />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={400} label={Helpers.amountToTwoDecimals(application_fee_with_gst)} />
                        </div>
                        {valuationFee !== 0 ? (
                          <>
                            <P2TextLabel
                              color="#323232"
                              label="Valuation fee"
                              fontSize={subTitleFontSize}
                              hasTooltip
                              tooltipContent={
                                <div className="polymer-financial-calculator-tooltip-content">
                                  A valuation fee may be required to determine the mortgage loan ceiling if a fresh HDB loan is required. The indicative
                                  valuation fee is for reference.
                                </div>
                              }
                            />
                            <div className="polymer-financial-calculator-fee-value">
                              <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={400} label={Helpers.amountToTwoDecimals(valuationFee)} />
                            </div>
                          </>
                        ) : null}
                      </div>

                      {/* Section 2 */}
                      <div className="polymer-financial-calculator-grid polymer-financial-calculator-fee-card">
                        <P2TextLabel color="#323232" label="Fees payable by current owners" fontSize={mainTitleFontSize} />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={mainTitleFontSize} bold={700} label={Helpers.amountToTwoDecimals(currentOwnersFees)} />
                        </div>
                        <div className="polymer-financial-calculator-separator" />
                        <P2TextLabel
                          color="#323232"
                          label="Lawyer fees payable to HDB"
                          fontSize={subTitleFontSize}
                          hasTooltip
                          tooltipContent={
                            <div className="polymer-financial-calculator-tooltip-content">
                              As the change in flat ownership is a conveyancing transaction, owners will be required to appoint and pay a fee to the lawyer who
                              acts for you in the flat purchase. The indicative conveyancing fee by HDB is for reference. Fees are payable by cash and/ or CPF
                              where applicable.
                            </div>
                          }
                        />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={400} label={Helpers.amountToTwoDecimals(lawyerFeesForCurrentOwners)} />
                        </div>
                        {values.isThereOutstandingLoan ? (
                          <>
                            <P2TextLabel
                              color="#323232"
                              label="Redemption of mortgage fees"
                              fontSize={subTitleFontSize}
                              hasTooltip
                              tooltipContent={
                                <div className="polymer-financial-calculator-tooltip-content">
                                  If there is an outstanding housing loan on the flat, owners will be required to discharge the existing loan as part of the
                                  conveyancing process.
                                </div>
                              }
                            />
                            <div className="polymer-financial-calculator-fee-value">
                              <P2TextLabel
                                color="#323232"
                                fontSize={subTitleFontSize}
                                bold={400}
                                label={Helpers.amountToTwoDecimals(redemptionOfMortgageFee)}
                              />
                            </div>
                          </>
                        ) : null}
                        <P2TextLabel
                          color="#323232"
                          label="Resale levy"
                          fontSize={subTitleFontSize}
                          hasTooltip
                          tooltipContent={
                            <div className="polymer-financial-calculator-tooltip-content">
                              If you are liable for additional resale levy/ premium, you will be advised of the amount payable during the application process.
                            </div>
                          }
                        />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={700} label="If applicable" />
                        </div>
                        <P2TextLabel
                          color="#323232"
                          label="Seller's Stamp Duty"
                          fontSize={subTitleFontSize}
                          hasTooltip
                          tooltipContent={
                            <div className="polymer-financial-calculator-tooltip-content">
                              Stamp duties payable to IRAS may apply, depending on the ownership change. Stamp duties include Buyer's Stamp Duty, Additional
                              Buyer's Stamp Duty and/ or Seller's Stamp Duty.
                            </div>
                          }
                        />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={700} label="If applicable" />
                        </div>
                      </div>

                      {/* Section 3 */}
                      <div className="polymer-financial-calculator-grid polymer-financial-calculator-fee-card">
                        <P2TextLabel color="#323232" label="Fees payable by proposed owners" fontSize={mainTitleFontSize} />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={mainTitleFontSize} bold={700} label={Helpers.amountToTwoDecimals(proposedOwnersFees)} />
                        </div>
                        <div className="polymer-financial-calculator-separator" />
                        <P2TextLabel
                          color="#323232"
                          label="Lawyer fees payable to HDB"
                          fontSize={subTitleFontSize}
                          hasTooltip
                          tooltipContent={
                            <div className="polymer-financial-calculator-tooltip-content">
                              As the change in flat ownership is a conveyancing transaction, owners will be required to appoint and pay a fee to the lawyer who
                              acts for you in the flat purchase. The indicative conveyancing fee by HDB is for reference. Fees are payable by cash and/ or CPF
                              where applicable.
                            </div>
                          }
                        />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel
                            color="#323232"
                            fontSize={subTitleFontSize}
                            bold={400}
                            label={Helpers.amountToTwoDecimals(lawyerFeesForProposedOwners)}
                          />
                        </div>
                        {values.isThereNewLoan ? (
                          <>
                            <P2TextLabel
                              color="#323232"
                              label="Fees payable for fresh mortgage"
                              fontSize={subTitleFontSize}
                              hasTooltip
                              tooltipContent={
                                <div className="polymer-financial-calculator-tooltip-content">
                                  Conveyancing fee, stamp fees and registration fees for a new HDB housing loan will be payable by cash or CPF, if required by
                                  proposed owners under the change in ownership. These fees are computed based on an estimated loan amount. This estimated loan
                                  amount is based on the existing Loan-To-Value limit of HDB housing loans, and is calculated using the aggregate median resale
                                  value of the flat type. Your actual loan value will depend on the approved or allowable loan limit, whichever is lower.
                                </div>
                              }
                            />
                            <div className="polymer-financial-calculator-fee-value">
                              <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={400} label={Helpers.amountToTwoDecimals(newMortgageFee)} />
                            </div>
                          </>
                        ) : null}
                        <P2TextLabel
                          color="#323232"
                          label="Buyer's Stamp Duty/ Additional Buyer's Stamp Duty"
                          fontSize={subTitleFontSize}
                          hasTooltip
                          tooltipContent={
                            <div className="polymer-financial-calculator-tooltip-content">
                              Stamp duties payable to IRAS may apply, depending on the ownership change. Stamp duties include Buyer's Stamp Duty, Additional
                              Buyer's Stamp Duty and/ or Seller's Stamp Duty.
                            </div>
                          }
                        />
                        <div className="polymer-financial-calculator-fee-value">
                          <P2TextLabel color="#323232" fontSize={subTitleFontSize} bold={700} label="If applicable" />
                        </div>
                      </div>

                      <Separator style={{ marginBottom: 32 }} />
                      <p className="underlined-title">Estimated Fees &mdash; Terms and Conditions</p>
                      <br />
                      <ol className="terms-conditions">
                        <li>
                          You must engage a Singapore practicing lawyer (solicitor) to act on your behalf in the conveyancing process. The estimated fees above
                          are based on the scenario that HDB is able to act in the conveyancing process, and thus payable to HDB.
                        </li>
                        <li>
                          If HDB is unable to act/ continue to act for you even though you had engaged HDB, you will need to engage a private lawyer(s) to act
                          for you in the transaction. Where HDB is unable to continue to act and discharges itself from acting in the transaction (at any point
                          in time and even on the actual completion day), you will still be liable for the administrative fees and half conveyancing fees even
                          if the transaction is not aborted and you proceed with private lawyers. Thus, in the interest of saving time and costs, you may wish
                          to consider the option of engaging private lawyers from the start.
                        </li>
                        <li>
                          Even if HDB does not represent you in the transaction and you engage private lawyers, your eligibility will still be assessed and
                          approved based on prevailing policy. The same will be conveyed to your private lawyers to proceed with the conveyancing process at
                          their end.
                        </li>
                        <li>
                          These estimated fees are inclusive of prevailing GST, and for reference only, as actual fees payable may differ from case to case. You
                          will be informed of the actual fees payable and the financial plan, during the application process to change the flat ownership and
                          upon submission of all the relevant information and documents.
                        </li>
                        <li>Fees payable by current owners must be paid in cash. Fees payable by proposed owners can be paid in cash and/ or CPF funds.</li>
                        <li>
                          If you are taking up an HDB housing loan, you will need to pay mortgage fees, including conveyancing fee, stamp fees and registration
                          fees. These fees are computed based on an estimated loan amount and are indicative only. Eligibility for an HDB housing loan is
                          subject to approval by HDB.
                        </li>
                        <li>
                          If you are taking a new housing loan from a financial institution (FI), you will need to check with your appointed lawyer on the stamp
                          duties and legal fees payable. In general, HDB does not act in an ownership change where there is a related housing loan from an FI.
                        </li>
                        <li>The payment plan for a housing loan provided by the FI is subject to the sole discretion of the FI.</li>
                        <li>
                          Before applying for a change in flat ownership, you are required to make payment for all outstanding amounts owed to HDB, such as
                          outstanding upgrading costs. All outstanding amounts owed to the Town Council should also be paid.
                        </li>
                        <li>
                          HDB shall not be liable for any loss, liability, cost, expense or damage suffered or incurred by any person (whether individual, body
                          corporate or otherwise), including but not limited to you or any other third party, in reliance on the information provided in this
                          e-Service, whether such information is provided by HDB, any FI, you or any other person (whether individual, body corporate or
                          otherwise).
                        </li>
                      </ol>
                    </>
                  ) : null}
                </div>
              }
              style={{ marginLeft: "auto", marginRight: "auto", marginBottom: "32px" }}
            />
            <Separator style={{ marginBottom: 32, marginLeft: 38, marginRight: 38 }} />
            <div style={{ marginBottom: 120 }}>
              {fromTransfer ? (
                <P2ButtonGroup>
                  <P2Button variant="outline-primary" disabled={!formValid} onClick={printAsPdf}>
                    Print as PDF
                  </P2Button>
                  <P2Button variant="primary-1" disabled={false} onClick={navigateBack}>
                    Back to dashboard
                  </P2Button>
                </P2ButtonGroup>
              ) : (
                <P2ButtonGroup>
                  <P2Button variant="outline-primary" disabled={false} onClick={navigateBack}>
                    Back
                  </P2Button>
                  <P2Button variant="primary-1" disabled={!formValid} onClick={printAsPdf}>
                    Print as PDF
                  </P2Button>
                </P2ButtonGroup>
              )}
            </div>
          </>
        )}
      </div>
    </ApplicationCooLayout>
  );
}

export default EstimateFees;
